/*
 * SAL — Lead Handoff Pipeline figure.
 * Geometry + animation ported 1:1 from SAL_Lead_Handoff_Pipeline v1 / handoff.js.
 * Uses the single shared Hairline kernel (src/components/feature/hairline/kernel.ts).
 * Hairline — https://github.com/lucasmarkes/hairline (commit bc782244), MIT License,
 * Copyright (c) Lucas Markes. Full license text is kept in the kernel file header.
 * Local fix: the reported phase now follows the card's real position (a forward jump such as
 * NEW → ASSIGNED no longer reports QUALIFIED while the sheet is still on the inquiry stack).
 */
/* eslint-disable */
import {
  Cam, fit, proj, facing, rings, prism, solid, put, mk, poly, fillet, seg, lerp,
  tween, tset, tval, tdone, register, pointer, disposer,
} from '@/components/feature/hairline/kernel';

export type SalState = 'new' | 'qualified' | 'assigned' | 'review';

export interface SalHandle {
  set: (v: number) => void;
  setState: (next: SalState) => void;
  setAutomatic: (on: boolean) => void;
  onState: (fn: (s: SalState) => void) => void;
  destroy: () => void;
}

interface Ctx { stage: Element; svg: SVGSVGElement; read: { textContent: string } }

export function mountHandoff({ stage, svg, read }: Ctx, value: number): SalHandle {
  const bag = disposer(), C = Cam(45, .5, 1.65), world = (u, v, z) => [(u + v) / Math.sqrt(2), (v - u) / Math.sqrt(2), z];
  fit(C, [world(-114, -60, 0), world(114, 80, 0), world(-114, -60, 34), world(114, 80, 34)], 200, 150);
  const P = proj(C), front = facing(C), g = mk('g', {}, svg), fixed = mk('g', {}, g), moving = mk('g', {}, g), position = tween(0), hold = tween(0);
  let state = 'new', reported = 'new', onState: (s: any) => void = () => {}, automatic = false, elapsed = 0, phase = -1, last = '', strength = value;
  function box(parent, x, y, w, d, z, h, r = 2) { const e = solid(parent), [a, b] = rings(x, y, x + w, y + d, r, .6); put(e, prism(P, front, a, b, z, z + h)); return e; }
  function card(parent, u, v, z, accent = false) {
    const [x, y] = world(u, v, 0), e = box(parent, x - 16, y - 22, 32, 44, z, .8, 2); e.sil.classList.toggle('hi', accent);
    const lines = mk('path', { class: 'nf lo' }, parent);
    lines.setAttribute('d', [[-10, -12, 2], [-10, -4, 10], [-10, 3, 10], [-10, 10, 6]].map(([a, b, c]) => seg(P(x + a, y + b, z + .8), P(x + c, y + b, z + .8))).join(''));
  }
  // Inquiry: actual paper stack, with offset page corners.
  for (let i = 0; i < 3; i++) card(fixed, -84 + i * 1.5, -i * 1.2, 2 + i * 1.2);
  // Qualification: a clipboard with a mechanical clip, not a stage pedestal.
  const [cx, cy] = world(0, 0, 0); box(fixed, cx - 21, cy - 28, 42, 56, 0, 2, 3);
  const [fx, fy] = world(84, 0, 0);
  // One continuous back leaf with its familiar index tab.
  const back = [[-23, -28], [-20, -28], [-20, -34], [-3, -34], [0, -28], [23, -28], [23, 28], [-23, 28]].map(([x, y]) => P(fx + x, fy + y, 1.5));
  mk('path', { class: 'sil', d: poly(fillet(back, [2, 1, 2, 2, 1, 2, 2, 2], 4)) }, fixed);
  const foreground = mk('g', {}, g);
  function draw(p, h) {
    moving.replaceChildren(); foreground.replaceChildren();
    const u = lerp(-84, 84, p / 2), v = h * 7, z = lerp(7, 5, p / 2) + Math.sin(Math.PI * (p % 1)) * 10 * strength + h * 5;
    card(moving, u, v, z, true);
    // Thin clamping jaw and a rounded, open spring handle form one metal clip.
    box(foreground, cx - 10, cy - 25, 20, 6, 7, 1, 1.5);
    const handle = [P(cx - 8, cy - 25, 8), P(cx - 8, cy - 29, 12), P(cx + 8, cy - 29, 12), P(cx + 8, cy - 25, 8)];
    mk('path', { class: 'nf sil', d: poly(fillet(handle, [1.2, 1.2, 1.2, 1.2], 5)) }, foreground);
    mk('path', { class: 'nf lo', d: seg(P(cx - 7, cy - 20, 8.1), P(cx + 7, cy - 20, 8.1)) }, foreground);
    // Folder front leaf: an open, thin cover. Its raised lip hides the lower sheet edge.
    const points = [P(fx - 23, fy - 8, 11), P(fx + 23, fy - 8, 11), P(fx + 23, fy + 28, 2), P(fx - 23, fy + 28, 2)];
    mk('path', { class: 'sil', d: poly(fillet(points, [2, 2, 2, 2], 4)) }, foreground);
    mk('path', { class: 'nf lo', d: seg(P(fx - 19, fy + 25, 3), P(fx + 19, fy + 25, 3)) }, foreground);
  }
  let B: any = null;
  function choose(next) {
    if (!['new', 'qualified', 'assigned', 'review'].includes(next) || next === state) return;
    state = next; const now = performance.now();
    tset(position, next === 'new' ? 0 : next === 'assigned' ? 2 : 1, now, 0); tset(hold, next === 'review' ? 1 : 0, now, 0); last = ''; B?.wake();
  }
  draw(0, 0); read.textContent = 'new';
  B = register(stage, (dt, now) => {
    if (automatic) {
      elapsed += Math.min(dt, .1) * 1000; const n = Math.floor(elapsed / 2600) % 4;
      if (n !== phase) { phase = n; choose(['new', 'qualified', 'assigned', 'review'][n]); }
    }
    const p = tval(position, now), h = tval(hold, now), key = [p, h, state, strength].join('|'); if (key !== last) { draw(p, h); last = key; }
    /* Status follows the sheet: moving forward, only the station actually reached is reported. */
    const target = state === 'new' ? 0 : state === 'assigned' ? 2 : 1;
    let actual = state;
    if (p < target - .01) actual = p < .99 ? 'new' : 'qualified';
    else if (state === 'review' && h < .99) actual = 'qualified';
    if (actual !== reported) { reported = actual; read.textContent = actual; onState(actual); }
    return automatic || !tdone(position, now) || !tdone(hold, now);
  }); bag.add(B.unregister);
  if (!(stage as Element).hasAttribute?.('data-controlled')) bag.add(pointer(stage, { move: ([x]) => choose(x > 285 ? 'assigned' : x > 150 ? 'qualified' : 'new'), leave: () => choose('new') }));
  bag.add(() => svg.replaceChildren());
  return {
    set: (v) => { strength = v; last = ''; B.wake(); },
    setState: (n) => { automatic = false; choose(n); },
    setAutomatic: (on) => { automatic = on; elapsed = 0; phase = -1; B.wake(); },
    onState: (fn) => { onState = fn; fn(reported as SalState); },
    destroy: bag.dispose,
  };
}